import React, { useEffect, useMemo, useRef, useState } from 'react';
import FilledButton from '../../../components/Buttons/FilledButton';
import classes from './styles.module.scss';

const normalizeTierName = (name) => (name || '').replace(/\s*\(1-year\)\s*/i, '').trim().toLowerCase();

export default function PlansSection({ tiers = [], tierSnapshot = {}, onUpgrade, onPortal, canUpgrade = true }) {
  const activeTierId = tierSnapshot.id || null;
  const activePriceId = tierSnapshot.stripePriceId || tierSnapshot.priceId || null;
  const subscriptionStatus = tierSnapshot.status || 'active';
  const cancelAt = tierSnapshot.cancelAt || null;
  const currentSeatLimit = tierSnapshot.seatLimit || 0;
  const isPendingCancellation = (subscriptionStatus && subscriptionStatus !== 'active') || !!cancelAt;

  const userChangedInterval = useRef(false);
  const [billingInterval, setBillingInterval] = useState('monthly'); // 'monthly' | 'annual'
  const lastSubscriptionKey = useRef(null);

  useEffect(() => {
    const activeIsAnnual =
      (tierSnapshot?.name && tierSnapshot.name.includes('(1-year)')) ||
      (activePriceId && String(activePriceId).toLowerCase().includes('annual'));
    const nextInterval = activeIsAnnual ? 'annual' : 'monthly';
    const subscriptionKey = `${activeTierId || ''}:${activePriceId || ''}`;

    if (lastSubscriptionKey.current !== subscriptionKey) {
      lastSubscriptionKey.current = subscriptionKey;
      userChangedInterval.current = false;
      setBillingInterval(nextInterval);
      return;
    }

    if (!userChangedInterval.current) {
      setBillingInterval(nextInterval);
    }
  }, [activePriceId, activeTierId, tierSnapshot?.name]);

  const annualTierByBaseName = useMemo(() => {
    const map = new Map();
    (tiers || []).forEach((tier) => {
      if (tier?.name?.includes('(1-year)')) {
        map.set(normalizeTierName(tier.name), tier);
      }
    });
    return map;
  }, [tiers]);

  const hasAnnualTiers = useMemo(() => (tiers || []).some((tier) => tier?.name?.includes('(1-year)')), [tiers]);
  const activeTierBaseName = normalizeTierName(tierSnapshot?.name);
  const isOnFreeTier = activeTierBaseName === 'free' || !activeTierBaseName;

  const toggleInterval = () => {
    userChangedInterval.current = true;
    setBillingInterval((prev) => (prev === 'annual' ? 'monthly' : 'annual'));
  };

  const visibleTiers = (tiers || [])
    .filter((tier) => !tier.name?.includes('(1-year)') && tier.name?.toLowerCase() !== 'free')
    .sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <>
      <h2 className={classes.sectionTitle}>Plans</h2>
      <div className={classes.cards}>
        {visibleTiers.map((tier) => {
          const annualTier = annualTierByBaseName.get(normalizeTierName(tier.name));
          const selectedTier = billingInterval === 'annual' && annualTier ? annualTier : tier;

          const isActiveMonthly =
            (activeTierId && tier.id === activeTierId) || (activePriceId && tier.priceId === activePriceId);
          const isActiveAnnual =
            annualTier &&
            ((activeTierId && annualTier.id === activeTierId) || (activePriceId && annualTier.priceId === activePriceId));
          const isActiveTier = isActiveMonthly || isActiveAnnual;
          const isDowngrade = selectedTier.seatLimit < currentSeatLimit;
          const activeLabel = isActiveAnnual ? 'Current plan (Annual)' : 'Current plan';

          return (
            <div
              key={tier.id}
              className={`${classes.card} ${classes.planCard} ${isActiveTier ? classes.activeCard : ''}`}
            >
              {isActiveTier && <div className={classes.currentBadge}>{activeLabel}</div>}
              <div className={classes.cardHeader}>
                <h3>{tier.name}</h3>
                {selectedTier.amount != null ? (
                  <div className={classes.price}>
                    ${((selectedTier.amount || 0) / 100).toFixed(0)}
                    <span>{billingInterval === 'annual' ? '/yr' : '/mo'}</span>
                  </div>
                ) : (
                  <div className={classes.price}>Contact sales</div>
                )}
              </div>
              <p className={classes.description}>{tier.description || 'Includes core features.'}</p>
              {isActiveTier && !isOnFreeTier && !isPendingCancellation ? (
                <div className={classes.currentPlanRow}>
                  <ul className={classes.limits}>
                    <li className={classes.activeText}>Up to {selectedTier.seatLimit} users</li>
                  </ul>
                  <button
                    type="button"
                    className={classes.cancelButton}
                    onClick={() => {
                      if (!canUpgrade) return;
                      onPortal?.();
                    }}
                    disabled={!canUpgrade}
                  >
                    Manage subscription
                  </button>
                </div>
              ) : (
                <ul className={classes.limits}>
                  <li className={isActiveTier ? classes.activeText : ''}>Up to {selectedTier.seatLimit} users</li>
                </ul>
              )}
              {canUpgrade && isOnFreeTier && !isActiveTier && !isDowngrade && (
                <FilledButton onClick={() => onUpgrade?.(selectedTier.id)} className={classes.upgradeButton}>
                  Upgrade
                </FilledButton>
              )}
            </div>
          );
        })}
      </div>
      {hasAnnualTiers && isOnFreeTier && (
        <div className={classes.intervalToggleRow}>
          <button type="button" className={classes.intervalToggleButton} onClick={toggleInterval}>
            {billingInterval === 'annual' ? 'Or pay monthly' : 'Or pay annually and save 10%'}
          </button>
        </div>
      )}
    </>
  );
}
